/* V3 candidate: physically staged foreground flags, a matte inflatable and a
   distant yacht. Source coordinates share one ground plane and cover crop. */
(() => {
  'use strict';
  if (window.CgauCoastalLife) { window.CgauCoastalLife.scan(document); return; }
  const TAU = Math.PI * 2;
  const clamp = (x, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));
  const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a)); return t * t * (3 - 2 * t); };
  const modulo = (n, m) => ((n % m) + m) % m;
  const instances = new Map();
  const SOURCE_W = 1672, SOURCE_H = 941, EYE_HEIGHT = 1.6;
  const colours = [[187,65,48], [224,215,185], [215,173,58], [82,118,140], [225,213,183], [105,130,113]];
  const hash = n => { const v = Math.sin(n * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
  let townGlow;
  // Artistic settlements on the photographed island, not mapped real towns.
  // Irregular shoreline lanes and a few uphill homes avoid a string of bulbs.
  const TOWN_LANES = [
    {count: 31, points: [[427,468],[461,465],[494,468],[532,466],[568,469]]},
    {count: 46, points: [[747,469],[789,466],[826,469],[870,466],[916,468]]},
    {count: 34, points: [[1199,467],[1238,469],[1276,466],[1318,468],[1361,469]]}
  ];
  const HILLSIDE_HOMES = [[475,454],[497,449],[525,453],[776,449],[795,445],[816,453],
    [839,442],[855,454],[881,450],[904,457],[1221,456],[1248,452],[1270,457],[1296,456]];

  class CoastalLife {
    constructor(scene) {
      this.scene = scene;
      this.section = scene.closest('[data-coastal-section]');
      this.image = scene.querySelector('[data-coast-image]');
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'coastal-life-canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.canvas.dataset.coastalLife = '';
      scene.append(this.canvas);
      this.ctx = this.canvas.getContext('2d', { alpha: true });
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.cleanups = [];
      this.elapsed = 0;
      this.frames = 0;
      this.raf = 0;
      this.last = 0;
      this.lastDraw = 0;
      this.userPaused = this.section?.dataset.paused === 'true';
      this.motionOverride = false;
      this.ambientPaused = this.section?.dataset.cgauMotionPaused === 'true';
      this.rendererPaused = ['paused','ambient-paused','reduced','hidden','offscreen'].includes(scene.dataset.motion);
      this.rendererSuspended = ['ambient-paused','hidden','offscreen'].includes(scene.dataset.motion);
      this.visible = false;
      this.pendingStill = true;
      this.suspended = false;
      this.destroyed = false;
      this.night = clamp(Number(scene.dataset.night) || 0);
      this.phase = Number.isFinite(Number(scene.dataset.phase)) ? Number(scene.dataset.phase) : .27;
      this.mode = this.section?.dataset.mode || 'day';
      this.tick = this.tick.bind(this);
      if (!this.ctx) { this.state('unavailable'); return; }
      this.prepareSphere();
      this.prepareFabric();
      this.prepareTowns();
      const bounds = scene.getBoundingClientRect();
      this.visible = bounds.bottom > 0 && bounds.top < innerHeight;
      this.resize();
      this.listen(scene, 'coast:time', event => {
        const previousNight = this.night, previousPhase = this.phase;
        if (Number.isFinite(event.detail?.night)) this.night = clamp(event.detail.night);
        if (Number.isFinite(event.detail?.phase)) this.phase = modulo(event.detail.phase, 1);
        const mode = this.section?.dataset.mode || 'day';
        const changedMode = mode !== this.mode;
        this.mode = mode;
        if (changedMode || previousNight !== this.night || previousPhase !== this.phase || event.detail?.still === true) this.pendingStill = true;
        // A reduced-motion still does not repaint on an externally running
        // clock. An explicit mode selection may choose a different still.
        if ((changedMode || event.detail?.still === true) && !this.running()) this.draw();
        this.schedule();
      });
      this.listen(scene, 'coast:patrol-ready', () => { this.pendingStill=true; this.sync(); });
      this.listen(scene, 'coast:weather', () => {
        this.pendingStill = true;
        // Share the established guards: retain storms received offscreen and
        // redraw one latest still when visible, without a second clock.
        if (!this.running() && !this.reduced()) this.sync();
      });
      this.listen(scene, 'coast:pause', event => {
        if (typeof event.detail?.paused !== 'boolean') return;
        this.userPaused = event.detail.paused;
        if (this.userPaused) this.motionOverride = false;
        else if (event.detail.userInitiated === true) this.motionOverride = true;
        this.sync();
      });
      this.listen(scene, 'coast:motion', event => {
        // Canvas2D remains useful when WebGL is unavailable, while still
        // sharing actual pause/visibility decisions with the water renderer.
        this.rendererPaused = event.detail?.playing === false &&
          ['paused','ambient-paused','reduced','hidden','offscreen'].includes(event.detail?.state);
        this.rendererSuspended = event.detail?.playing === false &&
          ['ambient-paused','hidden','offscreen'].includes(event.detail?.state);
        this.sync();
      });
      const ambient = event => {
        if (typeof event.detail?.paused === 'boolean') this.ambientPaused = event.detail.paused;
        this.sync();
      };
      if (this.section) this.listen(this.section, 'cgau:ambient-motion-change', ambient);
      this.listen(window, 'cgau:ambient-motion-change', event => { if (event.target === window) ambient(event); });
      this.listen(document, 'visibilitychange', () => this.sync());
      this.listen(this.reduce, 'change', () => {
        this.motionOverride = false;
        this.pendingStill = true;
        this.sync();
      });
      if (this.image) this.listen(this.image, 'load', () => this.resize());
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }, {threshold: 0});
        this.io.observe(scene);
      } else this.visible = true;
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.resize());
        this.ro.observe(scene);
      } else this.listen(window, 'resize', () => this.resize());
      this.sync();
    }
    listen(target, type, fn) {
      target.addEventListener(type, fn);
      this.cleanups.push(() => target.removeEventListener(type, fn));
    }
    state(value) {
      this.scene.dataset.lifeState = value;
      this.canvas.dataset.lifeState = value;
    }
    reduced() { return this.reduce.matches && !this.motionOverride; }
    drawable() {
      return Boolean(this.ctx && this.photo && !this.destroyed && !this.suspended && !document.hidden &&
        this.visible && !this.ambientPaused && !this.rendererSuspended);
    }
    running() {
      return Boolean(this.ctx && !this.destroyed && !this.suspended && !document.hidden &&
        this.visible && !this.userPaused && !this.ambientPaused && !this.rendererPaused && !this.reduced());
    }
    sync() {
      if (this.destroyed) return;
      if (!this.ctx) { this.state('unavailable'); return; }
      this.state(this.suspended || document.hidden ? 'hidden' : this.reduced() ? 'reduced' :
        this.userPaused ? 'paused' : this.ambientPaused ? 'ambient-paused' : !this.visible ? 'offscreen' :
        this.rendererPaused ? 'paused' : 'running');
      if (!this.running()) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
        this.last = 0;
      } else this.schedule();
      // Explicit input is retained while hidden, ambient-paused or offscreen.
      // On re-entry, paint its latest value once even when user-paused/reduced.
      if (this.pendingStill && this.drawable()) this.draw();
    }
    schedule() {
      if (!this.raf && this.running()) { this.last = 0; this.lastDraw = 0; this.raf = requestAnimationFrame(this.tick); }
    }
    tick(now) {
      this.raf = 0;
      if (!this.running()) { this.sync(); return; }
      if (this.last) this.elapsed += Math.min(.1, (now - this.last) / 1000);
      this.last = now;
      if (now - this.lastDraw >= 1000 / 30 - .5) { this.lastDraw = now; this.draw(); }
      this.raf = requestAnimationFrame(this.tick);
    }
    resize() {
      if (!this.ctx || this.destroyed) return;
      const box = this.scene.getBoundingClientRect();
      this.width = Math.max(1, box.width);
      this.height = Math.max(1, box.height);
      const dpr = Math.min(devicePixelRatio || 1, 1.5, 2200 / this.width, 1600 / this.height);
      this.dpr = dpr;
      this.canvas.width = Math.round(this.width * dpr);
      this.canvas.height = Math.round(this.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const iw = this.image?.naturalWidth || 1672, ih = this.image?.naturalHeight || 941;
      const cover = Math.max(this.width / iw, this.height / ih);
      this.photo = { width: iw * cover, height: ih * cover, x: (this.width - iw * cover) / 2, y: (this.height - ih * cover) / 2 };
      this.sourceScale = this.photo.width / SOURCE_W;
      this.pendingStill = true;
      this.draw();
    }
    photoY(fraction) { return this.photo.y + this.photo.height * fraction; }
    pixelsPerMetre(groundY) { return (groundY - (Number(this.scene.dataset.coastHorizon) || .507) * SOURCE_H) / EYE_HEIGHT; }
    inCrop(x, width = 0) {
      const screenX = this.photo.x + x * this.sourceScale;
      return screenX + width * this.sourceScale > 0 && screenX < this.width;
    }
    draw() {
      if (!this.drawable()) { this.pendingStill = true; return; }
      const c = this.ctx, w = this.width, h = this.height;
      c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      c.clearRect(0, 0, w, h);
      c.setTransform(this.dpr * this.sourceScale, 0, 0, this.dpr * this.sourceScale,
        this.dpr * this.photo.x, this.dpr * this.photo.y);
      const daylight = 1 - smooth(.18, .62, this.night);
      const shore = (Number(this.scene.dataset.coastShore) || .812) * SOURCE_H;
      const flagGround = .85 * SOURCE_H;
      const ballGround = .911 * SOURCE_H;
      const beachVisible = this.photo.y + ballGround * this.sourceScale < h && ballGround > shore;
      const time = this.reduced() ? 1.8 : this.elapsed;
      this.scene.dataset.lifeFlags = '0';
      this.scene.dataset.lifeBall = 'off';
      this.scene.dataset.lifeBoat = 'off';
      this.drawTowns(time);
      if (daylight > .02) {
        c.save(); c.globalAlpha = daylight;
        this.drawBoat(time);
        if (beachVisible) {
          const scaleAtFlags = this.pixelsPerMetre(flagGround);
          const flagWidth = .75 * scaleAtFlags;
          const poleHeight = 2.4 * scaleAtFlags;
          let visibleFlags = 0;
          for (const [index, fraction] of [.10, .82].entries()) {
            const patrol=window.CgauCoastalPatrol;
            const standing=patrol?.flagStandingForScene
              ? patrol.flagStandingForScene(this.scene,index,this.phase,this.mode)
              : patrol?.flagStanding(index,this.phase,this.mode);
            if (standing===false) continue;
            const x = fraction * SOURCE_W;
            if (!this.inCrop(x - 6, flagWidth + 12)) continue;
            this.drawFlag(x, flagGround, flagWidth, poleHeight, time, index * 1.7);
            visibleFlags++;
          }
          this.scene.dataset.lifeFlags = String(visibleFlags);
          this.scene.dataset.lifeFlagHeight = poleHeight.toFixed(2);
          this.scene.dataset.lifeFlagGround = flagGround.toFixed(2);
          this.scene.dataset.lifeFlagTop = (flagGround - poleHeight).toFixed(2);
          let progress = -1;
          if (this.reduced()) progress = .13;
          else if (this.mode === 'cycle') {
            if (this.phase > .14 && this.phase < .36) progress = (this.phase - .14) / .22;
          } else {
            const local = modulo(time - 8, 72);
            if (local < 15) progress = local / 15;
          }
          if (progress >= 0 && progress <= 1) {
            const radius = .4 * this.pixelsPerMetre(ballGround) / 2;
            const distance = progress * (SOURCE_W + radius * 4);
            const x = -radius * 2 + distance;
            const groundVariation = Math.sin(x * .031) * .27 + Math.sin(x * .073) * .12;
            const y = ballGround - radius + groundVariation;
            if (this.inCrop(x - radius, radius * 2)) {
              this.drawBall(x, y, radius, distance / radius);
              this.scene.dataset.lifeBall = progress.toFixed(3);
            }
            this.scene.dataset.lifeBallDiameter = (radius * 2).toFixed(2);
            this.scene.dataset.lifeBallGround = ballGround.toFixed(2);
            this.scene.dataset.lifeBallSourceX = x.toFixed(2);
          }
        }
        c.restore();
      }
      this.frames++;
      this.scene.dataset.lifeFrame = String(this.frames);
      this.canvas.dataset.lifeFrame = String(this.frames);
      this.scene.dataset.lifeTime = this.elapsed.toFixed(3);
      this.scene.dataset.lifePhase = this.phase.toFixed(5);
      this.pendingStill = false;
    }
    prepareTowns() {
      if (!townGlow) {
        const tile = document.createElement('canvas');
        tile.width = tile.height = 64;
        const c = tile.getContext('2d');
        if (c) {
          const pixels = c.createImageData(64,64);
          for (let y=0;y<64;y++) for (let x=0;x<64;x++) {
            const r = Math.hypot(x-31.5,y-31.5)/31.5;
            if (r>=1) continue;
            const at=(y*64+x)*4, core=1-smooth(0,.23,r);
            const falloff=(1/(1+64*r*r)-1/65)/(1-1/65)*(1-smooth(.65,1,r));
            pixels.data[at]=255; pixels.data[at+1]=176+core*64; pixels.data[at+2]=76+core*105;
            pixels.data[at+3]=Math.round(falloff*255);
          }
          c.putImageData(pixels,0,0); townGlow=tile;
        }
      }
      this.townGlow=townGlow;
      this.townLights=[];
      for (const [cluster,lane] of TOWN_LANES.entries()) for (let i=0;i<lane.count;i++) {
        const at=i/(lane.count-1)*(lane.points.length-1), segment=Math.min(lane.points.length-2,Math.floor(at));
        const t=at-segment, a=lane.points[segment], b=lane.points[segment+1], id=this.townLights.length;
        this.townLights.push({id,cluster,x:a[0]+(b[0]-a[0])*t+(hash(id+131)-.5)*2.7,
          y:a[1]+(b[1]-a[1])*t+(hash(id+271)-.5)*3.8,hill:false,
          strength:.58+hash(id+503)*.42,radius:.46+hash(id+601)*.44});
      }
      for (const [x,y] of HILLSIDE_HOMES) {
        const id=this.townLights.length;
        this.townLights.push({id,x,y,hill:true,strength:.48+hash(id+503)*.28,radius:.43+hash(id+601)*.28});
      }
      this.townReflections=this.townLights.filter(light=>!light.hill&&light.id%6===0).flatMap(light=>
        Array.from({length:12},(_,i)=>({light,index:i,jitter:hash(light.id*31+i+991),phase:hash(light.id*61+i+337)*TAU})));
      const bounds=this.townLights.reduce((b,p)=>[Math.min(b[0],p.x),Math.min(b[1],p.y),Math.max(b[2],p.x),Math.max(b[3],p.y)],[Infinity,Infinity,-Infinity,-Infinity]);
      this.scene.dataset.lifeTownBounds=JSON.stringify(bounds.map(n=>+n.toFixed(2)));
      this.scene.dataset.lifeTownTotal=String(this.townLights.length);
    }
    drawTowns(time) {
      const c=this.ctx,data=this.scene.dataset;
      const storm=clamp(Number(this.section?.dataset.weatherStorm)||0),rain=clamp(Number(this.section?.dataset.weatherRain)||0);
      const weather=(1-storm*.22)*(1-rain*.33),level=smooth(.23,.84,this.night);
      data.lifeTownLevel=level.toFixed(4);data.lifeTownWeather=weather.toFixed(4);
      data.lifeTownLights='0';data.lifeTownReflections='0';data.lifeTownEnergy='0.000';
      data.lifeTownReflectionMinY='';data.lifeTownReflectionMaxY='';
      if (level<.002) return;
      let count=0,energy=0;
      c.save();
      for (const light of this.townLights) {
        if (!this.inCrop(light.x-9,18)) continue;
        const ignition=smooth(.22+hash(light.id+723)*.08,.77+hash(light.id+911)*.1,this.night);
        // Slow, low-amplitude atmospheric variation. Lamps never blink out.
        const variation=.97+Math.sin(time*.37+light.id*1.83)*.02+Math.sin(time*.13+light.id*.71)*.01;
        const intensity=ignition*light.strength*variation*weather;
        if (intensity<.002) continue;
        const halo=light.hill?4.6:5.3+light.strength*2;
        c.globalAlpha=intensity*.88;
        if (this.townGlow) c.drawImage(this.townGlow,light.x-halo,light.y-halo,halo*2,halo*2);
        c.globalAlpha=intensity;
        c.fillStyle=light.id%7===0?'#ffeac4':light.id%3===0?'#ffd599':'#ffc776';
        c.beginPath();c.ellipse(light.x,light.y,light.radius,light.radius*.67,0,0,TAU);c.fill();
        count++;energy+=intensity;
      }
      c.restore();
      const horizon=(Number(data.coastHorizon)||.507)*SOURCE_H,shore=(Number(data.coastShore)||.812)*SOURCE_H;
      let reflections=0,minY=Infinity,maxY=-Infinity;
      c.save();c.beginPath();c.rect(0,horizon,SOURCE_W,Math.max(0,shore-horizon));c.clip();
      for (const glint of this.townReflections) {
        const light=glint.light,depth=(glint.index+.35)/12;
        const reach=29+light.strength*36;
        const y=horizon+1.3+depth*depth*reach+Math.sin(time*1.8+glint.phase)*(.1+depth*.4);
        const x=light.x+Math.sin(time*.61+glint.phase)*(1+depth*3.4)+(glint.jitter-.5)*depth*5;
        const breadth=(.65+depth*4.7)*(.6+glint.jitter*.9),thickness=.32+depth*.38;
        if (!this.inCrop(x-breadth,2*breadth)) continue;
        const glitter=.42+.58*Math.pow(.5+.5*Math.sin(time*1.35+glint.phase),2);
        c.globalAlpha=level*weather*(1-rain*.25)*light.strength*(1-depth)*glitter*.18;
        c.fillStyle=glint.index%3?'#e8af69':'#f6cd8a';
        c.fillRect(x-breadth/2,y,breadth,thickness);
        reflections++;minY=Math.min(minY,y);maxY=Math.max(maxY,y+thickness);
      }
      c.restore();
      data.lifeTownLights=String(count);data.lifeTownEnergy=energy.toFixed(3);data.lifeTownReflections=String(reflections);
      if (reflections) {data.lifeTownReflectionMinY=minY.toFixed(3);data.lifeTownReflectionMaxY=maxY.toFixed(3);}
    }
    ellipseShadow(x, y, rx, ry, alpha) {
      const c = this.ctx;
      c.save(); c.translate(x, y); c.scale(rx, ry);
      const gradient = c.createRadialGradient(0, 0, .05, 0, 0, 1);
      gradient.addColorStop(0, `rgba(54,35,20,${alpha})`);
      gradient.addColorStop(.5, `rgba(54,35,20,${alpha * .45})`);
      gradient.addColorStop(1, 'rgba(54,35,20,0)');
      c.fillStyle = gradient; c.beginPath(); c.arc(0, 0, 1, 0, TAU); c.fill(); c.restore();
    }
    prepareFabric() {
      const tile = document.createElement('canvas');
      tile.width = tile.height = 64;
      const ctx = tile.getContext('2d');
      if (!ctx) return;
      const pixels = ctx.createImageData(64, 64);
      for (let y = 0; y < 64; y++) for (let x = 0; x < 64; x++) {
        const at = (y * 64 + x) * 4;
        const lightThread = (x + y) % 3 === 0;
        pixels.data[at] = lightThread ? 242 : 64;
        pixels.data[at + 1] = lightThread ? 229 : 49;
        pixels.data[at + 2] = lightThread ? 196 : 29;
        pixels.data[at + 3] = 7 + hash(x + y * 73) * 17;
      }
      ctx.putImageData(pixels, 0, 0);
      this.weave = this.ctx.createPattern(tile, 'repeat');
    }
    drawFlag(x, ground, fw, ph, time, offset) {
      const c = this.ctx, top = ground - ph, fh = fw * 2 / 3;
      const poleWidth = this.pixelsPerMetre(ground) * .027;
      this.ellipseShadow(x + 4, ground + 1.5, 11, 3, .24);
      c.save();
      // Dawn changes the material lighting, never the opacity of a solid
      // foreground flag. Inheriting the daytime fade lets the sun shine
      // through both the fabric and pole.
      c.globalAlpha = 1;
      const flagLight = 1 - smooth(0, .65, this.night) * .43;
      const material = rgb => `rgb(${rgb.map(n => Math.round(n * flagLight)).join(' ')})`;
      const cast = c.createLinearGradient(x, ground, x + 128, ground + 66);
      cast.addColorStop(0, 'rgba(67,48,29,.23)');
      cast.addColorStop(1, 'rgba(67,48,29,0)');
      c.strokeStyle = cast; c.lineWidth = poleWidth * .8;
      c.beginPath(); c.moveTo(x, ground); c.lineTo(x + 128, ground + 66); c.stroke();
      const pole = c.createLinearGradient(x - poleWidth / 2, 0, x + poleWidth / 2, 0);
      pole.addColorStop(0, material([113,116,103])); pole.addColorStop(.28, material([202,196,172]));
      pole.addColorStop(.52, material([212,207,185])); pole.addColorStop(.76, material([153,156,141])); pole.addColorStop(1, material([102,111,100]));
      c.fillStyle = pole;
      c.fillRect(x - poleWidth / 2, top, poleWidth, ph);
      c.fillStyle = '#b7b8a6'; c.beginPath(); c.ellipse(x, top, poleWidth / 2, 1.6, 0, 0, TAU); c.fill();
      // A little weight and uneven wind bend the whole cloth. The two flags
      // share prevailing direction, but their travelling folds are out of phase.
      const clothTop = top + 12;
      const point = (u, v) => {
        const envelope = Math.pow(u, .8);
        const first = Math.sin(u * 8.2 - time * 2.15 + offset);
        const second = Math.sin(u * 17.1 - time * 3.1 + offset * 1.7);
        const ripple = (first * fw * .042 + second * fw * .013) * envelope;
        return [x + poleWidth * .50 + u * fw * .91 + first * u * fw * .017,
          clothTop + v * fh + ripple * (.72 + v * .28) + fw * .065 * u * u];
      };
      // Continuous colour bands avoid tiny transparent antialiasing gaps
      // between hundreds of cloth quads, especially against the bright sun.
      const cols = 40;
      for (let band = 0; band < 2; band++) {
        const v0 = band * .5, v1 = band === 0 ? .51 : 1;
        const gradient = c.createLinearGradient(...point(0,.5), ...point(1,.5));
        for (let stop = 0; stop <= 40; stop++) {
          const u = stop / 40;
          const fold = Math.cos(u * 8.2 - time * 2.15 + offset) * .16
            + Math.cos(u * 17.1 - time * 3.1 + offset * 1.7) * .05;
          const edge = Math.pow(u, 3) * -.018;
          const shade = clamp(.91 + fold * Math.pow(u, .5) + edge - band * .01, .67, 1.07);
          const rgb = band === 0 ? [202,49,30] : [235,187,52];
          gradient.addColorStop(u, material(rgb.map(n => n * shade)));
        }
        c.fillStyle = gradient; c.beginPath();
        for (let col = 0; col <= cols; col++) { const p = point(col / cols, v0); col ? c.lineTo(...p) : c.moveTo(...p); }
        for (let col = cols; col >= 0; col--) c.lineTo(...point(col / cols, v1));
        c.closePath(); c.fill();
      }
      const outline = () => {
        c.beginPath();
        for (let i = 0; i <= cols; i++) { const p = point(i / cols, 0); i ? c.lineTo(...p) : c.moveTo(...p); }
        for (let i = cols; i >= 0; i--) c.lineTo(...point(i / cols, 1));
        c.closePath();
      };
      if (this.weave) {
        c.save(); outline(); c.clip(); c.fillStyle = this.weave;
        c.fillRect(x, clothTop - 20, fw + 12, fh + 50); c.restore();
      }
      c.lineWidth = .65;
      for (const v of [.015, .5, .985]) {
        c.strokeStyle = v === .5 ? 'rgba(113,74,37,.29)' : 'rgba(243,214,139,.36)';
        c.beginPath();
        for (let i = 0; i <= cols; i++) { const p = point(i / cols, v); i ? c.lineTo(...p) : c.moveTo(...p); }
        c.stroke();
      }
      c.strokeStyle = 'rgba(97,83,55,.25)'; outline(); c.stroke();
      c.strokeStyle = 'rgba(214,207,180,.62)'; c.lineWidth = .85;
      c.beginPath(); c.moveTo(x + poleWidth * .7, top + 10);
      c.quadraticCurveTo(x + poleWidth + 1.5, ground - 155, x + poleWidth * .55, ground - 5); c.stroke();
      for (const yy of [clothTop + 2, clothTop + fh - 2]) {
        c.strokeStyle = '#b1ac95'; c.lineWidth = 1;
        c.beginPath(); c.ellipse(x + poleWidth * .45, yy, 3.2, 2, -.25, 0, TAU); c.stroke();
      }
      c.restore();
    }
    prepareSphere() {
      const size = 112;
      this.ballCanvas = document.createElement('canvas');
      this.ballCanvas.width = this.ballCanvas.height = size;
      this.ballContext = this.ballCanvas.getContext('2d');
      this.ballPixels = this.ballContext.createImageData(size, size);
      this.normals = [];
      const lx = -.46, ly = -.62, lz = .636;
      for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
        const nx = (x + .5 - size / 2) / (size / 2 - 1), ny = (y + .5 - size / 2) / (size / 2 - 1);
        const rr = nx * nx + ny * ny;
        if (rr >= 1) continue;
        const nz = Math.sqrt(1 - rr);
        const diffuse = Math.max(0, nx * lx + ny * ly + nz * lz);
        const underside = smooth(.30, .99, ny);
        const specular = Math.pow(Math.max(0, nx * -.26 + ny * -.35 + nz * .900), 18) * .045;
        this.normals.push({index:(y * size + x) * 4, x:nx, y:ny, z:nz,
          // A front fill and gradual underside occlusion retain curvature
          // where direct sunlight falls to zero. No flat ambient-only half.
          shade: .36 + diffuse * .60 + nz * .10 - underside * .07 + (hash(x + y * 113) - .5) * .016,
          underside, specular,
          alpha: clamp((1 - Math.sqrt(rr)) * size / 1.5) * 255});
      }
    }
    drawBall(x, y, radius, angle) {
      const c = this.ctx;
      const contactY = y + radius;
      this.ellipseShadow(x + radius * .68, contactY + 10, radius * 1.34, radius * .28, .25);
      this.ellipseShadow(x + radius * .07, contactY + 1.2, radius * .84, radius * .15, .37);
      this.ellipseShadow(x, contactY + .25, radius * .42, 3.1, .53);
      c.save(); c.fillStyle = 'rgba(42,31,18,.43)';
      c.beginPath(); c.ellipse(x, contactY + .2, radius * .25, 1.25, 0, 0, TAU); c.fill(); c.restore();
      const co = Math.cos(angle), si = Math.sin(angle), pixels = this.ballPixels.data;
      for (const p of this.normals) {
        // Inverse Z rotation produces real rolling about the screen-facing
        // axis; a tilted stripe axis makes the coloured panels tumble in 3D.
        const rx = co * p.x + si * p.y, ry = -si * p.x + co * p.y;
        const sx = rx * .91 + p.z * .415, sz = p.z * .91 - rx * .415;
        const sy = ry * .819 + sz * .574, zz = sz * .819 - ry * .574;
        const longitude = modulo(Math.atan2(zz, sx) / TAU + 1, 1) * 6;
        const seam = Math.min(longitude % 1, 1 - longitude % 1);
        const polar = Math.abs(sy);
        const rgb = polar > .94 ? [221,213,187] : colours[Math.floor(longitude) % 6];
        const seamShade = seam < .018 && polar < .94 ? .91 + seam / .018 * .09 : 1;
        for (let channel = 0; channel < 3; channel++) {
          const neutral = rgb[0] * .30 + rgb[1] * .59 + rgb[2] * .11;
          const material = rgb[channel] + (neutral - rgb[channel]) * p.underside * .24;
          const bounceTint = channel === 0 ? 1.025 : channel === 1 ? 1 : .925;
          const diffuseColour = material * p.shade * seamShade * (1 + (bounceTint - 1) * p.underside);
          pixels[p.index + channel] = clamp(diffuseColour + (255 - diffuseColour) * p.specular, 0, 255);
        }
        pixels[p.index + 3] = p.alpha;
      }
      this.ballContext.putImageData(this.ballPixels, 0, 0);
      c.drawImage(this.ballCanvas, x - radius, y - radius, radius * 2, radius * 2);
    }
    drawBoat(time) {
      const c = this.ctx;
      const horizon = (Number(this.scene.dataset.coastHorizon) || .507) * SOURCE_H;
      const waterOffset = 3.8;
      const metres = waterOffset / EYE_HEIGHT;
      const mast = 10 * metres, hull = 7 * metres;
      const x = modulo(time * .85 + SOURCE_W * .72 + 40, SOURCE_W + 80) - 40;
      const y = horizon + waterOffset + Math.sin(time * .7) * .12;
      this.scene.dataset.lifeBoatWaterline = (horizon + waterOffset).toFixed(3);
      this.scene.dataset.lifeBoatMast = mast.toFixed(2);
      this.scene.dataset.lifeBoatHull = hull.toFixed(2);
      if (!this.inCrop(x - hull * 2, hull * 3)) return;
      c.save(); c.translate(x, y); c.rotate(Math.sin(time * .54) * .008);
      c.globalAlpha *= .60;
      // At this distance, the yacht reads through its cloth curvature and
      // soft tonal changes. Rigging stays finer than a source-image pixel.
      c.filter = 'blur(.18px)';
      const dusk = clamp(this.night * .75);
      const colour = (r, g, b) => `rgb(${Math.round(r + (103-r)*dusk)},${Math.round(g + (124-g)*dusk)},${Math.round(b + (130-b)*dusk)})`;
      const sunX = Number(this.scene.dataset.sunX);
      const lightFromLeft = !Number.isFinite(sunX) || sunX * SOURCE_W < x;

      // Broken, low-contrast wake ties the hull to the channel without a
      // bright underline or the oversized shadow of a foreground object.
      c.strokeStyle = 'rgba(166,185,172,.22)'; c.lineWidth = .42;
      c.beginPath(); c.moveTo(-hull * 1.20, .68);
      c.quadraticCurveTo(-hull * .85, 1.03, -hull * .43, .73); c.stroke();
      c.strokeStyle = 'rgba(112,143,139,.20)'; c.lineWidth = .34;
      c.beginPath(); c.moveTo(-hull * .92, 1.35);
      c.quadraticCurveTo(-hull * .59, 1.07, -hull * .26, 1.42); c.stroke();

      // A narrow, curved hull: darker waterline, sunlit deck and a small
      // recessed cockpit, with no heavy outline around the silhouette.
      const hullShade = c.createLinearGradient(0, -.65, 0, 1.7);
      hullShade.addColorStop(0, colour(157,169,156));
      hullShade.addColorStop(.28, colour(130,151,144));
      hullShade.addColorStop(1, colour(79,111,110));
      c.fillStyle = hullShade;
      c.beginPath(); c.moveTo(-hull * .50, -.10);
      c.bezierCurveTo(-hull * .15, .16, hull * .25, -.32, hull * .50, -.50);
      c.quadraticCurveTo(hull * .39, .87, hull * .30, 1.17);
      c.quadraticCurveTo(-hull * .06, 1.67, -hull * .37, 1.10);
      c.quadraticCurveTo(-hull * .46, .65, -hull * .50, -.10); c.fill();
      c.fillStyle = colour(167,176,164);
      c.beginPath(); c.moveTo(-hull * .19, -.28);
      c.quadraticCurveTo(-hull * .15, -1.16, -hull * .06, -1.22);
      c.lineTo(hull * .12, -1.03); c.lineTo(hull * .19, -.32); c.closePath(); c.fill();
      c.strokeStyle = 'rgba(64,94,98,.37)'; c.lineWidth = .40;
      c.beginPath(); c.moveTo(-hull * .07, -.74); c.lineTo(hull * .09, -.72); c.stroke();

      // The shrouds precede the cloth, so they do not draw an icon-like
      // triangular outline over the sail. The forestay remains barely visible.
      c.strokeStyle = 'rgba(124,145,138,.38)'; c.lineWidth = .28;
      c.beginPath(); c.moveTo(-.32, -mast + .30);
      c.lineTo(hull * .45, -.64); c.moveTo(-.32, -mast * .72);
      c.lineTo(-hull * .35, -.21); c.stroke();

      // Mainsail: a curved leech, bowed foot and shaded belly suggest filled
      // cloth instead of a flat white triangle. Its luff follows the mast.
      const mainShade = c.createLinearGradient(lightFromLeft ? -hull * .49 : .2, -mast * .48,
        lightFromLeft ? .2 : -hull * .49, -mast * .38);
      mainShade.addColorStop(0, colour(161,179,169));
      mainShade.addColorStop(.38, colour(207,210,190));
      mainShade.addColorStop(.68, colour(184,196,180));
      mainShade.addColorStop(1, colour(115,148,142));
      c.fillStyle = mainShade;
      c.beginPath(); c.moveTo(-.68, -mast + .80);
      c.bezierCurveTo(-hull * .13, -mast * .76, -hull * .34, -mast * .43, -hull * .49, -mast * .135);
      c.quadraticCurveTo(-hull * .27, -mast * .083, -.76, -mast * .111);
      c.quadraticCurveTo(-.48, -mast * .53, -.68, -mast + .80); c.fill();
      // Curved panel seams carry only a hint of fabric structure at full size.
      c.strokeStyle = 'rgba(117,146,138,.26)'; c.lineWidth = .25;
      c.beginPath(); c.moveTo(-hull * .145, -mast * .61);
      c.quadraticCurveTo(-hull * .083, -mast * .574, -.63, -mast * .60);
      c.moveTo(-hull * .32, -mast * .34);
      c.quadraticCurveTo(-hull * .17, -mast * .286, -.72, -mast * .32); c.stroke();

      // The smaller headsail overlaps the mast-side shadow, with an inflated
      // centre and a gently rolled leech. It is deliberately asymmetric.
      const jibShade = c.createLinearGradient(.30, -mast * .34, hull * .40, -mast * .25);
      jibShade.addColorStop(0, colour(110,143,140));
      jibShade.addColorStop(.28, colour(179,195,180));
      jibShade.addColorStop(.63, colour(205,210,190));
      jibShade.addColorStop(1, colour(146,169,159));
      c.fillStyle = jibShade;
      c.beginPath(); c.moveTo(.28, -mast * .795);
      c.bezierCurveTo(hull * .12, -mast * .60, hull * .34, -mast * .33, hull * .41, -mast * .12);
      c.quadraticCurveTo(hull * .24, -mast * .085, hull * .055, -mast * .14);
      c.bezierCurveTo(hull * .118, -mast * .33, hull * .058, -mast * .62, .28, -mast * .795); c.fill();
      c.strokeStyle = 'rgba(119,147,140,.22)'; c.lineWidth = .25;
      c.beginPath(); c.moveTo(hull * .075, -mast * .41);
      c.quadraticCurveTo(hull * .19, -mast * .356, hull * .265, -mast * .373); c.stroke();

      // A slender mast and boom finish the structure; neither is pure white.
      c.strokeStyle = colour(137,159,150); c.lineWidth = .44;
      c.beginPath(); c.moveTo(-.28, -mast); c.lineTo(-.28, -.46); c.stroke();
      c.strokeStyle = 'rgba(126,151,142,.55)'; c.lineWidth = .36;
      c.beginPath(); c.moveTo(-.45, -mast * .102);
      c.lineTo(-hull * .48, -mast * .115); c.stroke();
      c.restore();
      this.scene.dataset.lifeBoat = (x / SOURCE_W).toFixed(4);
    }
    suspend() { this.suspended = true; this.sync(); }
    resume() { this.suspended = false; this.pendingStill = true; this.sync(); }
    destroy() {
      if (this.destroyed) return;
      this.destroyed = true; cancelAnimationFrame(this.raf); this.raf = 0;
      this.io?.disconnect(); this.ro?.disconnect(); this.cleanups.forEach(fn => fn());
      this.canvas.remove(); this.state('destroyed'); instances.delete(this.scene);
    }
  }
  function scan(scope) {
    const scenes = scope instanceof Element && scope.matches('.coast-scene') ? [scope] : [...scope.querySelectorAll('.coast-scene')];
    scenes.forEach(scene => { if (!instances.has(scene)) instances.set(scene, new CoastalLife(scene)); });
  }
  window.CgauCoastalLife = { scan, destroyAll: () => [...instances.values()].forEach(instance => instance.destroy()) };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), {once:true});
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...instances.values()].filter(instance => event.target.contains(instance.scene)).forEach(instance => instance.destroy());
  });
  window.addEventListener('pagehide', event => [...instances.values()].forEach(instance => event.persisted ? instance.suspend() : instance.destroy()));
  window.addEventListener('pageshow', () => { scan(document); instances.forEach(instance => instance.resume()); });
})();
